import type React from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { ChevronRightGlyph } from './StationPageGlyphs'
import styles from './StationBreadcrumb.module.css'

interface Props {
  /** URL locale segment, e.g. `en`. */
  locale: string
  /** Display name of the current station, e.g. `Baker Street`. */
  stationName: string
}

/**
 * Breadcrumb row of the station-page header (Figma 8597:44807):
 * `Home › <station>`. The design's middle "Stations" crumb is dropped
 * (decision 2026-08-04): its index page does not exist, and users only
 * reach this page from Google or the map — a dead crumb would 302 to
 * the map (crawl-budget lesson TASK-592). Restore it when the index
 * lands. The current station never links to itself.
 */
export function StationBreadcrumb({ locale, stationName }: Props): React.JSX.Element {
  const { t } = useTranslation('station-page')
  return (
    <nav aria-label={t('breadcrumb_aria')} className={styles.breadcrumb}>
      <ol className={styles.list}>
        <li className={styles.item}>
          <Link to={`/${locale}`} className={styles.link}>
            {t('breadcrumb_home')}
          </Link>
        </li>
        <li className={styles.item}>
          <ChevronRightGlyph size={15} className={styles.chevron} />
          <span aria-current="page" className={styles.current}>
            {stationName}
          </span>
        </li>
      </ol>
    </nav>
  )
}
